import React, { useEffect, useRef, useState } from 'react'

import classNames from 'clsx'
import PropTypes from 'prop-types'

import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  Grow,
  MenuItem,
  MenuList,
  Paper,
  Popper,
} from '@mui/material'
import { makeStyles } from '@mui/styles'

import ArrowDown from '~/src/UI/Icons/ArrowDown'

const displayName = 'SplitButton'
const useStyles = makeStyles(theme => ({
  popper: {
    zIndex: 9999
  },
  buttonGroup: {
    whiteSpace: 'nowrap',
  },
  button: {
    '& svg': {
      marginRight: theme.spacing(1),
    }
  }
}), { name: displayName })

/**
 * A split button that opens a tiny menu when the icon button is clicked
 * @component
 * @alias SplitButton
 * @named_export SplitButtonComponent
 */
const SplitButtonComponent = ({
  className,
  disabled = false,
  options,
  ...passthru
}) => {
  const classes = useStyles(passthru)

  const [open, setOpen] = useState(false)
  const [selectedKey, setSelectedKey] = useState(() => (Array.isArray(options) ? options[0]?.key : null))
  const anchorRef = useRef(null)

  useEffect(() => {
    if (selectedKey === null || !options.some(option => option.key === selectedKey)) {
      setSelectedKey(options.length > 0 ? options[0].key : null)
    }
  }, [options, selectedKey])

  const handleMenuItemClick = (event, key) => {
    setSelectedKey(key)
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen)
  }

  const handleClose = event => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setOpen(false)
  }

  const selectedOption = options.find(option => option.key === selectedKey)

  if (!selectedOption) {
    return null
  }

  if (options.length === 1) {
    return (
      <Button
        variant={selectedOption?.variant ?? 'contained'}
        color="primary"
        onClick={selectedOption.onClick}
        href={selectedOption.href}
        component={selectedOption.href ? 'a' : undefined}
        className={classNames({
          [classes.button]: true,
          [className]: Boolean(className),
        })}
        disabled={disabled || selectedOption.disabled}
      >
        {selectedOption?.icon ?? null}
        {selectedOption.label}
      </Button>
    )
  }

  return (
    <>
      <ButtonGroup
        variant={selectedOption?.variant ?? 'contained'}
        color="primary"
        ref={anchorRef}
        aria-label="split button"
        className={classNames({
          [classes.buttonGroup]: true,
          [className]: Boolean(className),
        })}
        disabled={disabled}
      >
        <Button
          onClick={selectedOption.onClick}
          href={selectedOption.href}
          component={selectedOption.href ? 'a' : undefined}
          className={classNames({
            [classes.button]: true,
            [className]: Boolean(className),
          })}
          disabled={selectedOption.disabled}
        >
          {selectedOption?.icon ?? null}
          {selectedOption.label}
        </Button>
        <Button
          color="primary"
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-label="select merge strategy"
          aria-haspopup="menu"
          onClick={handleToggle}
        >
          <ArrowDown />
        </Button>
      </ButtonGroup>
      <Popper
        className={classes.popper}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList id="split-button-menu">
                  {options.filter(({ key }) => (key !== selectedKey)).map(({ key, label }) => (
                    <MenuItem
                      key={key}
                      selected={key === selectedKey}
                      onClick={event => handleMenuItemClick(event, key)}
                    >
                      {label}
                    </MenuItem>
                  ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </>
  )
}
SplitButtonComponent.displayName = displayName
SplitButtonComponent.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    href: PropTypes.string,
    icon: PropTypes.element,
    disabled: PropTypes.bool,
  })).isRequired,
  disabled: PropTypes.bool,
}

export default React.memo(SplitButtonComponent)
